import {
  isPersonAttributeType,
  type GeneratedStatus,
  type InstanceDetail,
  type ResolvedSegment,
} from "@keydials/api-client";
import { paletteForSegment } from "../instanceModel";
import { utcDay } from "../lib/ui";
import { personAttributeState, personLabel } from "./personAttribute";

/**
 * "Coming up" (KEY-COMINGUP): every dated node on a dial, in date order, with how far off each date is —
 * counted in the DIAL's own calendar, which is what its time zone is for. Built entirely from the instance
 * detail the workspace already holds: the review dates are on the status rows, the names on the resolved
 * structure, the colours on the palettes, and who each item is for on its Person fields. No round trip,
 * and exactly the visibility the detail already applied.
 *
 * A row is a node WITH a date; a node without one has nothing to come up. Past dates are always included
 * (they are the ones that most need seeing) and the horizon cuts off the future only.
 */

export interface ComingUpRow {
  /** Stable per node and axis, for React keys. */
  key: string;
  sectionKey: string;
  segmentKey: string;
  /** Null for an action point's own date. */
  itemKey: string | null;
  /** Null for an action point's own date. */
  dimensionKey: string | null;
  /** The item's name, or the action point's. */
  title: string;
  /** Where it lives: "Section · Action point" for an item, the section for an action point. */
  where: string;
  /** The axis the date is on, when the dial scores items on more than one; otherwise null. */
  axisLabel: string | null;
  /** The whole-day date, `YYYY-MM-DD`. */
  reviewDate: string;
  /** Days from the dial's today to the date: negative when past, 0 today. */
  daysUntil: number;
  /** The status the node is showing on that axis, resolved against its palette; null when the palette lacks it. */
  status: GeneratedStatus | null;
  /** Who the item is for, from any Person field it carries — names from the dial's Who's who list. */
  people: string[];
}

export type ComingUpBucket = "overdue" | "today" | "week" | "later";

/** Which bucket a row sits in, by how far off its date is. */
export function bucketOf(row: ComingUpRow): ComingUpBucket {
  if (row.daysUntil < 0) return "overdue";
  if (row.daysUntil === 0) return "today";
  if (row.daysUntil <= 7) return "week";
  return "later";
}

/** The dial's own calendar day for an instant: `YYYY-MM-DD` in its time zone, UTC when it has none set. */
export function dialToday(timeZoneId: string | null | undefined, now: Date = new Date()): string {
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone: timeZoneId || "UTC", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
  } catch {
    // An unresolvable zone id: the same fallback the server's sweeps make, rather than a blank tab.
    return new Intl.DateTimeFormat("en-CA", { timeZone: "UTC", year: "numeric", month: "2-digit", day: "2-digit" }).format(now);
  }
}

/** Whole days between two `YYYY-MM-DD` days, positive when `to` is later. */
export function daysBetween(from: string, to: string): number {
  const a = Date.UTC(Number(from.slice(0, 4)), Number(from.slice(5, 7)) - 1, Number(from.slice(8, 10)));
  const b = Date.UTC(Number(to.slice(0, 4)), Number(to.slice(5, 7)) - 1, Number(to.slice(8, 10)));
  return Math.round((b - a) / 86_400_000);
}

/**
 * Every dated node within the horizon (past dates always), in date order then by name. `horizonDays` is
 * how far into the future to look; pass a large number for everything.
 */
export function comingUpRows(detail: InstanceDetail, horizonDays: number, now: Date = new Date()): ComingUpRow[] {
  const today = dialToday(detail.timeZoneId, now);
  const rows: ComingUpRow[] = [];

  const sections = new Map(detail.sections.map((s) => [s.key, s]));
  const segmentOf = (sectionKey: string, segmentKey: string): ResolvedSegment | undefined =>
    sections.get(sectionKey)?.segments.find((g) => g.key === segmentKey);
  const sectionName = (sectionKey: string) => sections.get(sectionKey)?.label ?? sectionKey;
  const segmentName = (g: ResolvedSegment | undefined, segmentKey: string) => g?.name || g?.label || segmentKey;

  // Person fields on items, so a row can say who it is for.
  const personDecls = (detail.nodeAttributes ?? []).filter((a) => a.target === "Item" && isPersonAttributeType(a.type));
  const peopleFor = (sectionKey: string, segmentKey: string, itemKey: string): string[] => {
    if (personDecls.length === 0) return [];
    const names: string[] = [];
    for (const decl of personDecls) {
      const value = (detail.nodeAttributeValues ?? []).find((v) =>
        v.nodeType === "Item" && v.sectionKey === sectionKey && v.segmentKey === segmentKey && v.itemKey === itemKey && v.attributeKey === decl.key);
      if (!value?.value) continue;
      for (const r of personAttributeState(value.value, detail.people).resolved) {
        if (r.person) names.push(personLabel(r.person));
      }
    }
    return [...new Set(names)];
  };

  const dimensions = detail.dimensions ?? [];
  const manyAxes = dimensions.length > 1;

  for (const s of detail.itemStatuses ?? []) {
    const day = utcDay(s.reviewDate);
    if (!day) continue;
    const daysUntil = daysBetween(today, day);
    if (daysUntil > horizonDays) continue;
    const segment = segmentOf(s.sectionKey, s.segmentKey);
    const item = segment?.items.find((i) => i.key === s.itemKey);
    if (!segment || !item) continue; // a hidden or removed node: nothing to open
    const dimension = dimensions.find((d) => d.key === s.dimensionKey);
    rows.push({
      key: `item:${s.sectionKey}/${s.segmentKey}/${s.itemKey}/${s.dimensionKey}`,
      sectionKey: s.sectionKey,
      segmentKey: s.segmentKey,
      itemKey: s.itemKey,
      dimensionKey: s.dimensionKey,
      title: item.name || item.key,
      where: `${sectionName(s.sectionKey)} · ${segmentName(segment, s.segmentKey)}`,
      axisLabel: manyAxes ? (dimension?.label ?? s.dimensionKey) : null,
      reviewDate: day,
      daysUntil,
      status: dimension?.palette.statuses.find((st) => st.key === s.statusKey) ?? null,
      people: peopleFor(s.sectionKey, s.segmentKey, s.itemKey),
    });
  }

  for (const s of detail.statuses) {
    const day = utcDay(s.reviewDate);
    if (!day) continue;
    const daysUntil = daysBetween(today, day);
    if (daysUntil > horizonDays) continue;
    const segment = segmentOf(s.sectionKey, s.segmentKey);
    if (!segment) continue;
    rows.push({
      key: `segment:${s.sectionKey}/${s.segmentKey}`,
      sectionKey: s.sectionKey,
      segmentKey: s.segmentKey,
      itemKey: null,
      dimensionKey: null,
      title: segmentName(segment, s.segmentKey),
      where: sectionName(s.sectionKey),
      axisLabel: null,
      reviewDate: day,
      daysUntil,
      status: paletteForSegment(detail, segment).find((st) => st.key === s.statusKey) ?? null,
      people: [],
    });
  }

  return rows.sort((a, b) => a.reviewDate.localeCompare(b.reviewDate) || a.title.localeCompare(b.title));
}

/** "3 days ago", "today", "tomorrow", "in 5 days" — the phrase beside a date. */
export function daysPhrase(daysUntil: number): string {
  if (daysUntil === 0) return "today";
  if (daysUntil === 1) return "tomorrow";
  if (daysUntil === -1) return "yesterday";
  if (daysUntil < 0) return `${-daysUntil} days ago`;
  return `in ${daysUntil} days`;
}

/** What the Overview strip says (KEY-COMINGUP): how many dated nodes are overdue, due today, or due in the next 7 days. */
export interface ComingUpCounts {
  overdue: number;
  today: number;
  week: number;
  /** True when there is nothing to say — the strip stays off the screen rather than reading "0 · 0 · 0". */
  empty: boolean;
}

export function comingUpCounts(detail: InstanceDetail, now: Date = new Date()): ComingUpCounts {
  const counts = { overdue: 0, today: 0, week: 0 };
  for (const row of comingUpRows(detail, 7, now)) {
    const b = bucketOf(row);
    if (b === "overdue") counts.overdue++;
    else if (b === "today") counts.today++;
    else if (b === "week") counts.week++;
  }
  return { ...counts, empty: counts.overdue + counts.today + counts.week === 0 };
}
